import { Component, inject, OnInit } from '@angular/core';
import { Tag } from 'primeng/tag';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import {
    AiAssistService,
    AI_ASSIST_ICON,
    VENDOR_DASHBOARD_URLS,
    VendorBalance,
    VendorBalanceStatus,
} from '@/app/services/ai-assist.service';

/**
 * Dashboard card showing each AI vendor's remaining prepaid credits + status
 * (ok / low / critical). Lets operators top up before generations start
 * failing — the AI Assist service blocks generation at `critical` and the
 * topbar dot turns amber/red, but this surfaces the actual numbers.
 */
@Component({
    standalone: true,
    selector: 'app-vendor-balance-widget',
    imports: [Tag, Button, Tooltip],
    template: `
        <div class="card flex flex-col gap-2">
            <div class="flex items-center justify-between mb-2">
                <span class="font-semibold text-lg flex items-center gap-2">
                    <i [class]="aiIcon"></i>
                    AI vendor credits
                </span>
                <p-button
                    icon="pi pi-refresh"
                    severity="secondary"
                    [text]="true"
                    [rounded]="true"
                    pTooltip="Refresh"
                    (click)="reload()"
                />
            </div>

            @if (aiAssist.vendorBalances(); as b) {
                <ul
                    class="flex flex-col divide-y divide-surface-200 dark:divide-surface-700 m-0 p-0 list-none"
                >
                    @for (v of b.vendors; track v.vendor) {
                        <li
                            class="py-3 flex items-center justify-between gap-3"
                        >
                            <div class="flex flex-col gap-0.5 min-w-0">
                                <span class="font-medium">{{
                                    vendorLabel(v.vendor)
                                }}</span>
                                @if (
                                    v.status !== 'unconfigured' &&
                                    v.low_threshold !== null
                                ) {
                                    <span class="text-xs text-muted-color">
                                        warn ≤ {{ v.low_threshold }} · block ≤
                                        {{ v.critical_threshold }}
                                    </span>
                                } @else if (v.detail) {
                                    <span class="text-xs text-muted-color">{{
                                        v.detail
                                    }}</span>
                                }
                            </div>
                            <div class="flex items-center gap-2 flex-shrink-0">
                                <span class="font-medium whitespace-nowrap">{{
                                    balanceLabel(v)
                                }}</span>
                                <p-tag
                                    [value]="v.status"
                                    [severity]="balanceSeverity(v.status)"
                                />
                                @if (dashboardUrl(v.vendor); as url) {
                                    <a
                                        [href]="url"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="text-muted-color hover:text-primary"
                                        [pTooltip]="
                                            'Open ' +
                                            vendorLabel(v.vendor) +
                                            ' console'
                                        "
                                        tooltipPosition="top"
                                        [attr.aria-label]="
                                            'Open ' +
                                            vendorLabel(v.vendor) +
                                            ' console'
                                        "
                                    >
                                        <i class="pi pi-external-link"></i>
                                    </a>
                                }
                            </div>
                        </li>
                    }
                </ul>
            } @else {
                <span class="text-sm text-muted-color">
                    {{
                        aiAssist.vendorBalancesLoaded()
                            ? 'No vendor balances.'
                            : 'Could not load vendor credits.'
                    }}
                </span>
            }
        </div>
    `,
})
export class VendorBalanceWidget implements OnInit {
    protected readonly aiAssist = inject(AiAssistService);
    protected readonly aiIcon = AI_ASSIST_ICON;

    ngOnInit(): void {
        this.reload();
    }

    protected reload(): void {
        void this.aiAssist.refreshVendorBalances(true);
    }

    protected vendorLabel(vendor: string): string {
        switch (vendor) {
            case 'flux':
                return 'FLUX';
            case 'recraft':
                return 'Recraft';
            case 'grok':
                return 'Grok';
            default:
                return vendor;
        }
    }

    protected dashboardUrl(vendor: string): string | null {
        return VENDOR_DASHBOARD_URLS[vendor] ?? null;
    }

    protected balanceLabel(v: VendorBalance): string {
        if (v.status === 'unconfigured') return 'n/a';
        if (v.balance === null) return '—';
        if (v.unit === 'usd') return `$${v.balance.toFixed(2)}`;
        return `${v.balance.toLocaleString()} ${v.unit}`;
    }

    protected balanceSeverity(
        status: VendorBalanceStatus,
    ): 'success' | 'warn' | 'danger' | 'secondary' {
        switch (status) {
            case 'ok':
                return 'success';
            case 'low':
                return 'warn';
            case 'critical':
                return 'danger';
            default:
                return 'secondary';
        }
    }
}
